import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PasswordReset() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const tokenMissing = !token;
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(tokenMissing ? "This reset link is missing its token." : "");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 10) return setError("Password must be at least 10 characters.");
    if (pw !== pw2) return setError("Passwords do not match.");
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/password/reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, newPassword: pw }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Reset failed.");
      }
      setDone(true);
      toast.success("Password set. You are signed in.");
      setTimeout(() => navigate("/home", { replace: true }), 1200);
    } catch (err) {
      setError(err.message || "Reset failed.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto py-12 text-center" data-testid="pwd-reset-success">
        <ShieldCheck className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--rs-amber)" }} />
        <h1 className="text-xl font-semibold mb-1">Password updated</h1>
        <p className="text-[var(--rs-text-3)] text-sm">Redirecting…</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8" data-testid="pwd-reset-page">
      <h1 className="text-2xl font-semibold mb-2">Set a new password</h1>
      <p className="text-[var(--rs-text-3)] text-sm mb-6">Choose a password of at least 10 characters.</p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-[var(--rs-text-3)]">New password</span>
          <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} required minLength={10}
            testId="pwd-reset-input-new" className="mt-1" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-[var(--rs-text-3)]">Confirm new password</span>
          <PasswordInput value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={10}
            testId="pwd-reset-input-confirm" className="mt-1" />
        </label>
        {error && (<div className="text-[12px] text-red-400" data-testid="pwd-reset-error">{error}</div>)}
        <button type="submit" disabled={busy || !token}
          data-testid="pwd-reset-submit"
          className="w-full h-10 rounded-md font-semibold text-sm"
          style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}>
          {busy ? "Setting…" : "Set new password"}
        </button>
      </form>
    </div>
  );
}
